import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import { happyfoxUrl, imgBaseUrl } from 'configs';
import { Formik } from 'formik';
import * as yup from 'yup';
import JoditEditor from 'jodit-react';
import NumberFormat from 'react-number-format';
import {reduce, isPlainObject, isEqual} from 'lodash';
import {
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  LinearProgress,
  Switch,
  Backdrop,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Icon,
  IconButton,
} from '@material-ui/core';
import { Breadcrumb } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import {
  getEditableStoreProductApi,
  editSubscriptionOptionApi,
  getFBTLinksApi,
  getStoreTaxClassesApi,
  addTasksToQueueApi,
  getStoreCustomerGroupsApi
} from 'api';
import {
  editUserStoreProduct,
  editUserStoreProductDescription,
  changeProductSubscriptionInstalmentPrice
} from 'redux/actions/products';
import { makeStyles } from '@material-ui/core/styles';
import {isCertificationOption} from 'helpers/certifications';
import ProductCategories from './productCategories';
import Subscription from './productSubscription';
import CustomOptions from './customOptions/customOptions';
import FrequentlyBoughtTogether from './frequentlyBoughtTogether';
import UpSell from './upSell';
import CrossSell from './crossSell';
import CertifiedInfo from './certifiedInfo';
import ProductImageGallery from './productImageGallery';
import PatientCertifications from './patientCertifications/patientCertifications';
import SearchEngineOptimization from './searchEngineOptimization';
import Instructions from './instructions';


type ProductToUpdate = {
  sku: string;
  custom_attributes?: { attribute_code: string; value: string }[];
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },

  productImage: {
    padding: '25px',
    width: '100%',
    maxHeight: '400px',
    objectFit: 'contain',
  },

  alignButtons: {
    minWidth: 'fit-content',
  },
}));

const config = {
  'showXPathInStatusbar': false,
  'spellcheck': false,
  'askBeforePasteHTML': false,
  'askBeforePasteFromWord': false,
  'disablePlugins': 'table,preview,table-keyboard-navigation,image,media,video,file,add-new-line,about,class-span,focus,delete,wrap-text-nodes,copy-format,clipboard,color,drag-and-drop,drag-and-drop-element,error-messages,font,format-block,fullsize,hotkeys,iframe,indent,hr,inline-popup,justify,limit,link,mobile,ordered-list,placeholder,redo-undo,resizer,search,select,size,resize-handler,source,stat,sticky,symbols,tooltip,xpath,image-properties,image-processor',
  'buttons': 'bold,italic,underline,ul,ol'
};

const diff = function (obj1, obj2) {
  return reduce(obj1, (result, value, key) => {
    if (isPlainObject(value)) {
      result[key] = diff(value, obj2[key]);
    } else if (!isEqual(value, obj2[key])) {
      result[key] = value;
    }

    return result;
  }, {});
};

interface Params {
  id: string;
}

const EditProduct = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const params = useParams<Params>();
  const history = useHistory();
  const { profile, userStoreData } = useSelector((state: IReduxState) => state.user);
  const { customerGroupsIds } = useSelector((state: IReduxState) => state.customers);

  const permissions = profile?.config?.permissions || {};
  const { stores = [] } = profile;
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  useEffect(() => {
    getEditableStoreProductApi({dispatch, sku: params.id});
    getFBTLinksApi({dispatch, sku: params.id});
    getStoreTaxClassesApi({dispatch});

    if (!customerGroupsIds.length) {
      getStoreCustomerGroupsApi({ dispatch });
    }
  }, []);

  const [open, setOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState('0');
  const [isPriceEdited, setIsPriceEdited] = useState(false);
  const [updateTitle, setUpdatedTitle] = useState(false);

  const handleChangeStatus = (event) => {
    if (event.target.checked) {
      const updatedProduct = { status: 1 };
      dispatch(editUserStoreProduct(updatedProduct));
    } else {
      const updatedProduct = { status: 2 };
      dispatch(editUserStoreProduct(updatedProduct));
    }
  };

  const handleChangeStock = (event) => {
    const {extension_attributes} = editable_store_product;
    const {checked} = event.target;

    if (checked) {
      const updatedProduct = {
        extension_attributes: {
          ...extension_attributes,
          stock_item: {
            ...extension_attributes.stock_item,
            manage_stock: true,
            is_in_stock: true,
            qty: 999
          }
        }
      };
      dispatch(editUserStoreProduct(updatedProduct));
    } else {
      const updatedProduct = {
        extension_attributes: {
          ...extension_attributes,
          stock_item: {
            ...extension_attributes.stock_item,
            manage_stock: true,
            is_in_stock: false,
            qty: 0
          }
        }
      };
      dispatch(editUserStoreProduct(updatedProduct));
    }
  };

  const handleExpansionClick = (ind) => (event, isExpanded) => {
    setExpandedIndex(isExpanded ? ind : false);
  };

  const parseProductDescription = (description) => {
    const matchUrl = magentoImgUrlRegex.exec(description);
    const matchImgSrc = magentoImgSrcRegex.exec(description);
    const defaultSrcForImg = descriptionImgSrcRegex.exec(description);
    const imgSrc = matchUrl ? `src="${stores[0]?.storeUrl}/media/${matchUrl[1]}"` : defaultSrcForImg[0];

    return matchImgSrc ? description.replace(matchImgSrc[0], imgSrc) : description.replace(imgSrc, imgSrc);
  };

  const {
    productCustomOptions: options,
    selectedProductCategoriesIds,
    editable_store_product_no_changes,
    editable_store_product,
    editable_store_product: { extension_attributes, type_id, sku },
    subscriptionOptions,
    fbtData,
    loadingEditableStoreProduct,
    error,
    storeTaxClasses = []
  } = useSelector((state: IReduxState) => state.products);

  const isMembership = sku?.includes('MEMBER');

  const description = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'description')?.value;
  const productTaxClass = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'tax_class_id') || {};
  const productTaxClassId = productTaxClass.value;
  const taxableClass = storeTaxClasses.find((c) => c.class_name.toLowerCase().includes(stores[0]?.storeCode));

  const shortDescription = editable_store_product.custom_attributes?.find((a) => a.attribute_code === 'short_description')?.value;
  const brand = (new URLSearchParams(window.location.search)).get('brand');
  const minProductPrice = +editable_store_product?.custom_attributes?.find((item) => item.attribute_code === 'catalog_min_price')?.value;
  const isOfficePickup = editable_store_product?.custom_attributes?.find((item) => item.attribute_code === 'isoffice')?.value;
  const isHidePriceMode = editable_store_product?.custom_attributes?.find((item) => item.attribute_code === 'am_hide_price_mode')?.value === '0';
  const magentoImgUrlRegex = new RegExp(/url\s*=\s*"(.+?)"/);
  const magentoImgSrcRegex = new RegExp(/src\s*=\s*"(.+?)}}"/);
  const descriptionImgSrcRegex = new RegExp(/src\s*=\s*"(.+?)"/);
  const pathForUrlRegex = new RegExp(/medias*\s*(.+?)"/);
  let validatedDescription = description || '';
  let validatedShortDescription = shortDescription || '';

  if (description?.includes('img')) {
    validatedDescription = parseProductDescription(description);
  }

  if (shortDescription?.includes('img')) {
    validatedShortDescription = parseProductDescription(shortDescription);
  }

  const saveEdits = async () => {
    setOpen(true);

    const prepareProductToUpdate = {
      ...editable_store_product,
      custom_attributes: editable_store_product.custom_attributes.map((a) => {
        if (editable_store_product.type_id === 'mpgiftcard' && a.attribute_code === 'gift_card_amounts') {
          return {...a, value: JSON.stringify(a.value)};
        }

        if (a.attribute_code === 'category_ids') {
          return {
            ...a,
            value: selectedProductCategoriesIds
          };
        }

        return a;
      })
    };

    const productKeysDiff = diff(prepareProductToUpdate, editable_store_product_no_changes);
    const productImgAttr = editable_store_product.custom_attributes?.find((attr) => attr.attribute_code === 'image');
    let productToUpdate: ProductToUpdate = {...productKeysDiff, sku: editable_store_product.sku};
    const editedAttrWithoutImg = productToUpdate?.custom_attributes?.filter((a) => a?.attribute_code !== 'image') || [{ attribute_code: 'image', value: 'no_selection' }];

    if (productImgAttr) {
      editedAttrWithoutImg.push(productImgAttr);
    }

    productToUpdate = {...productToUpdate, custom_attributes: editedAttrWithoutImg};

    const task = {
      body: productToUpdate,
      name: editable_store_product.name,
      sku: editable_store_product.sku,
      requestType: 'UPDATE_PRODUCT'
    };

    addTasksToQueueApi({dispatch, tasks: [task]});

    if (subscriptionOptions.length !== 0 && isPriceEdited) {
      editSubscriptionOptionApi({dispatch, subscriptionOptions});
    }

    history.goBack();
    setOpen(false);
  };

  const editProductPrice = (newPrice) => {
    const updatedProduct = { price: newPrice };
    dispatch(editUserStoreProduct(updatedProduct));
    dispatch(changeProductSubscriptionInstalmentPrice(newPrice));
    setIsPriceEdited(true);
  };

  const editProductTitle = (newTitle) => {
    const updatedTitle = { name: newTitle };
    dispatch(editUserStoreProduct(updatedTitle));
  };

  const editProductQty = (qty) => {
    if (!qty) {
      const updatedProduct = {
        extension_attributes: {
          ...extension_attributes,
          stock_item: {
            ...extension_attributes.stock_item,
            manage_stock: true,
            is_in_stock: false,
            qty: 0
          }
        }
      };
      dispatch(editUserStoreProduct(updatedProduct));
    } else {
      const updatedProduct = {
        extension_attributes: {
          ...extension_attributes,
          stock_item: {
            ...extension_attributes.stock_item,
            manage_stock: true,
            is_in_stock: true,
            qty
          }
        }
      };
      dispatch(editUserStoreProduct(updatedProduct));
    }
  };

  const editGiftCardRange = (name, value) => {
    const updatedProduct = {
      custom_attributes: editable_store_product.custom_attributes.map((a) => {
        if (a.attribute_code === name) {
          return {...a, value: Number(value).toFixed(4)};
        }

        if (a.attribute_code === 'gift_card_amounts' && name === 'min_amount') {
          const valueToUpdate = a.value.map((el) => {
            return el.record_id === '0' ? {...el, amount: value, price: value} : el;
          });
          return {...a, value: valueToUpdate};
        }

        if (a.attribute_code === 'gift_card_amounts' && name === 'max_amount') {
          const valueToUpdate = a.value.map((el) => {
            return el.record_id === '1' ? {...el, amount: value, price: value} : el;
          });
          return {...a, value: valueToUpdate};
        }

        return a;
      }),
    };
    dispatch(editUserStoreProduct(updatedProduct));
  };

  const editProductDescription = (newDescription, attributeCode) => {
    const description = newDescription.replace(/style="(.*?)"/gm, '');
    let validatedDescription = description;

    if (description.includes('img')) {
      const matchNewSrc = descriptionImgSrcRegex.exec(description);
      const matchNewPath = pathForUrlRegex.exec(description);
      const srcForStoreImg = matchNewPath ? `src="{{media url="${matchNewPath[1]}"}}"` : matchNewSrc[0];
      validatedDescription = description.replace(matchNewSrc[0], srcForStoreImg);
    }

    dispatch(editUserStoreProductDescription(validatedDescription, attributeCode));
  };

  const changeProductTaxClass = (isEnabled) => {
    const updatedProduct = {
      custom_attributes: editable_store_product.custom_attributes.map((item) => {
        if (item.attribute_code === 'tax_class_id') {
          return {...item, value: isEnabled ? String(taxableClass?.class_id) : 0};
        }

        return item;
      })
    };

    dispatch(editUserStoreProduct(updatedProduct));
  };

  const changeIsOfficePickup = (isEnabled) => {
    const updatedProduct = {
      custom_attributes: editable_store_product.custom_attributes.map((item) => {
        if (item.attribute_code === 'isoffice') {
          return {...item, value: isEnabled ? 1 : 0};
        }

        if (item.attribute_code === 'am_hide_price_mode' && !isEnabled) {
          return {...item, value: '' };
        }

        return item;
      })
    };

    dispatch(editUserStoreProduct(updatedProduct));
  };

  const handleChangeHidePrice = (isEnabled) => {
    const updatedCustomAttributes = editable_store_product.custom_attributes.map((item) => {
      switch (item.attribute_code) {
        case 'am_hide_price_mode':
          return { ...item, value: isEnabled ? '0' : '1' };
        case 'am_hide_price_customer_gr':
          return { ...item, value: `-1,00,1,2,3,4,${customerGroupsIds.join(',')}` };
        default:
          return item;
      }
    });

    const hasHidePriceMode = updatedCustomAttributes.some((item) => item.attribute_code === 'am_hide_price_mode');
    const hasHidePriceCustomerGr = updatedCustomAttributes.some((item) => item.attribute_code === 'am_hide_price_customer_gr');

    if (!hasHidePriceMode) {
      updatedCustomAttributes.push({ attribute_code: 'am_hide_price_mode', value: isEnabled ? '0' : '1' });
    }

    if (!hasHidePriceCustomerGr) {
      updatedCustomAttributes.push({ attribute_code: 'am_hide_price_customer_gr', value: `-1,00,1,2,3,4,${customerGroupsIds.join(',')}` });
    }

    const updatedProduct = { custom_attributes: updatedCustomAttributes };

    dispatch(editUserStoreProduct(updatedProduct));
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y px-10 pb-25">
      {loadingEditableStoreProduct && <LinearProgress className="mt-10" />}
      {!loadingEditableStoreProduct && error &&
        <div className="p-10">Something went wrong</div>
      }
      {!loadingEditableStoreProduct && !error && extension_attributes &&
      <>
        <div className="mb-sm-30">
          <Breadcrumb
            routeSegments={[
              { name: 'Listings', path: '/store-products/list/active' },
              { name: `${editable_store_product?.name}` }
            ]}
          />
        </div>
        <Formik
          initialValues={{
            qty: extension_attributes.stock_item.qty,
            price: editable_store_product.price,
            status: editable_store_product.status,
            inStock: !!extension_attributes.stock_item.is_in_stock,
            name: editable_store_product.name
          }}
          onSubmit={saveEdits}
          enableReinitialize
          validationSchema={
            yup.object({
              price: yup
                .number()
                .positive()
                .required('Price is required')
                .min(minProductPrice || 0, `MAP cannot go lower than $${minProductPrice || 0}`)
            })
          }
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            handleChange
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={5}>
                <Grid item sm={4} xs={12} className="text-center">
                  <img
                    src={
                      editable_store_product.custom_attributes?.filter((i) => i.attribute_code === 'image').length
                        ? `${mediaStoreUrl}${editable_store_product.custom_attributes?.filter((i) => i.attribute_code === 'image').map((i) => i.value)}`
                        : logo}
                    alt={editable_store_product.name}
                    className={classes.productImage}
                    onError={(e) => {
                      (e.target as HTMLImageElement).onerror = null;
                      (e.target as HTMLImageElement).src = logo;
                    }}
                  />
                </Grid>
                <Grid item sm={8} xs={12}>
                  {/* <Typography variant="h4" className="py-4">{editable_store_product.name}</Typography> */}
                  <div className="flex items-center justify-between">
                    <Typography variant="h4" className="py-4">
                      {updateTitle ?
                        <div className="flex">
                          <TextField
                            variant="outlined"
                            name="name"
                            className="w-full max-w-80full"
                            value={values.name}
                            onChange={(e) => {
                              handleChange(e);
                            }}
                          />
                          <Button
                            onClick={(e) => {
                              setUpdatedTitle(false);
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() => {
                              editProductTitle(values.name.trim());
                              setUpdatedTitle(false);
                            }}
                            disabled={!values.name.trim()}
                          >
                            Save
                          </Button>
                        </div>
                        :
                        <Typography
                          variant="h4"
                          className="py-4 flex items-center"
                        >
                          {editable_store_product.name}
                          <IconButton
                            title="Edit Product Title"
                            className="justify-self-end"
                            onClick={() => {
                              setUpdatedTitle(true);
                            }}
                          >
                            <Icon>edit</Icon>
                          </IconButton>
                        </Typography>
                      }
                    </Typography>
                    <div className={classes.alignButtons}>
                      {permissions?.help &&
                        <Button className="mr-5">
                          <Link target="_blank" to={{ pathname: happyfoxUrl}}>
                            FAQ
                          </Link>
                        </Button>
                      }
                      <Button onClick={() => history.goBack()} className="mr-5">
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveEdits}
                        disabled={Object.keys(errors).length !== 0}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                  <Typography variant="body1" className="text-muted mb-2">SKU: {editable_store_product.sku}</Typography>
                  <Typography variant="body1" className="text-muted mb-2">Brand: {brand || '-'}</Typography>
                  <Divider />
                  <Typography variant="body1" className="py-4">
                    Enable:
                    <Switch
                      onChange={handleChangeStatus}
                      checked={values.status === 1}
                      color="primary"
                      name="checkedB"
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                      className="ml-2"
                      title="Click here to delete an item from your store"
                    />
                  </Typography>
                  <Divider />
                  {permissions?.productManageStock && !userStoreData?.isOldMigrated && type_id === 'simple' &&
                    <>
                      <Typography variant="body1" className="py-4 flex items-center">
                        Quantity:
                        <NumberFormat
                          allowNegative={false}
                          name="qty"
                          value={values.qty}
                          className="mx-2"
                          inputProps={{className: 'text-center min-w-100' }}
                          onValueChange={(val) => {
                            setFieldValue('qty', val.floatValue);
                            editProductQty(val.floatValue);
                          }}
                          onBlur={handleBlur}
                          error={touched.qty && Boolean(errors.qty)}
                          helperText={touched.qty && errors.qty}
                          customInput={TextField}
                        />
                      </Typography>
                      <Divider />
                      <Typography variant="body1" className="py-4">
                        In Stock:
                        <Switch
                          onChange={handleChangeStock}
                          checked={values.inStock}
                          color="primary"
                          name="is_in_stock"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                          className="ml-2"
                        />
                      </Typography>
                      <Divider />
                    </>
                  }
                  {editable_store_product.type_id !== 'mpgiftcard' ?
                    <Typography variant="body1" className="py-4 flex items-center">
                      Price:
                      <NumberFormat
                        prefix="$"
                        fixedDecimalScale
                        decimalScale={2}
                        thousandSeparator
                        name="price"
                        value={values.price}
                        className="mx-2"
                        disabled={editable_store_product.type_id === 'configurable'}
                        inputProps={{className: 'text-center min-w-100' }}
                        onValueChange={(val) => {
                          setFieldValue('price', val.floatValue);
                          editProductPrice(val.floatValue);
                        }}
                        onBlur={handleBlur}
                        error={touched.price && Boolean(errors.price)}
                        helperText={touched.price && errors.price}
                        customInput={TextField}
                      />
                    </Typography>
                    :
                    <Typography variant="body1"className="py-4 flex items-center">
                      Range From: $
                      <NumberFormat
                        thousandSeparator
                        name="min_amount"
                        decimalScale={2}
                        allowNegative={false}
                        value={(editable_store_product.custom_attributes.find((a) => a.attribute_code === 'min_amount').value)}
                        inputProps={{className: 'text-center' }}
                        onValueChange={(val) => {
                          editGiftCardRange('min_amount', val.value);
                        }}
                        customInput={TextField}
                      />
                      To: $
                      <NumberFormat
                        thousandSeparator
                        name="max_amount"
                        decimalScale={2}
                        allowNegative={false}
                        value={(editable_store_product.custom_attributes.find((a) => a.attribute_code === 'max_amount').value)}
                        inputProps={{className: 'text-center' }}
                        onValueChange={(val) => {
                          editGiftCardRange('max_amount', val.value);
                        }}
                        customInput={TextField}
                      />
                    </Typography>
                  }
                  <Divider />
                  <Typography variant="body1"className="py-4 flex items-center">
                    Is Taxable:&nbsp;
                    <Switch
                      onChange={(e) => changeProductTaxClass(e.target.checked)}
                      checked={productTaxClassId === String(taxableClass?.class_id)}
                      disabled={!taxableClass}
                      color="primary"
                      name="Is Taxable"
                      className="ml-2"
                    />
                  </Typography>
                  <Divider />
                  <Typography variant="body1"className="py-4 flex items-center">
                    {'Not Purchasable Online: '}
                    <Switch
                      onChange={(e) => changeIsOfficePickup(e.target.checked)}
                      checked={isOfficePickup == 1}
                      color="primary"
                      name="isoffice"
                      className="ml-2"
                    />
                  </Typography>

                  {isOfficePickup == 1 &&
                  <Typography variant="body1"className="py-4 flex items-center">
                    {'Hide Price: '}
                    <Switch
                      onChange={(e) => handleChangeHidePrice(e.target.checked)}
                      checked={isHidePriceMode}
                      color="primary"
                      name="am_hide_price_mode"
                      className="ml-2"
                    />
                  </Typography>}
                  <Divider />
                  <Typography variant="body1" className=" pt-4 font-bold">Product Short Description:</Typography>
                  <JoditEditor
                    value={validatedShortDescription}
                    config={config}
                    onBlur={(newContent) => editProductDescription(newContent, 'short_description')}
                  />
                  <Divider />
                  <Typography variant="body1" className=" pt-4 font-bold">Product Description:</Typography>
                  <JoditEditor
                    value={validatedDescription}
                    config={config}
                    onBlur={(newContent) => editProductDescription(newContent, 'description')}
                  />
                </Grid>
              </Grid>
              <Accordion
                elevation={25}
                className="mt-10 border-radius-4"
                expanded={expandedIndex === '1'}
                onChange={handleExpansionClick('1')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to review/add/delete the categories under which this item can be listed">Categories</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProductCategories />
                </AccordionDetails>
              </Accordion>
              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '2'}
                onChange={handleExpansionClick('2')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to edit treatment image">
                    Images ({editable_store_product.media_gallery_entries.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProductImageGallery product={editable_store_product} />
                </AccordionDetails>
              </Accordion>
              {editable_store_product.type_id !== 'mpgiftcard' && !isMembership &&
              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '3'}
                onChange={handleExpansionClick('3')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to make the purchase of an item on a recurrent basis">Subscription</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Subscription />
                </AccordionDetails>
              </Accordion>
              }

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '4'}
                onChange={handleExpansionClick('4')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to review/add/delete available options to your item (e.g.: size, color, area of application, etc.)">
                    Custom Options ({options.filter((option) => !isCertificationOption(option)).length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomOptions />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '5'}
                onChange={handleExpansionClick('5')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to link this product to other products that customers might want to purchase">Frequently Bought Together Items ({fbtData.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FrequentlyBoughtTogether product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '6'}
                onChange={handleExpansionClick('6')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to explore additional options to your item (e.g.: upgrade, add-on, etc.)">
                    Upsell Items ({editable_store_product.product_links.filter((item) => item.link_type === 'upsell').length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <UpSell product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '7'}
                onChange={handleExpansionClick('7')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to explore similar items">
                    Cross-sell Items ({editable_store_product.product_links.filter((item) => item.link_type === 'crosssell').length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CrossSell product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '8'}
                onChange={handleExpansionClick('8')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="SEO/Meta">
                    SEO/Meta
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SearchEngineOptimization product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '9'}
                onChange={handleExpansionClick('9')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Usage/Instructions">
                    Usage/Instructions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Instructions product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '10'}
                onChange={handleExpansionClick('10')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to enable/disable Patient Certification">Patient Certification</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <PatientCertifications product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                elevation={25}
                className="mt-4 border-radius-4"
                expanded={expandedIndex === '11'}
                onChange={handleExpansionClick('11')}
              >
                <AccordionSummary
                  expandIcon={<Icon>expand_more</Icon>}
                >
                  <Typography variant="h5" className="text-brand m-2" title="Click here to add a custom message to your customer">Additional Description</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CertifiedInfo product={editable_store_product} />
                </AccordionDetails>
              </Accordion>

              <Backdrop className={classes.backdrop} open={open}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </form>)}
        </Formik>
      </>}
    </div>
  );
};


export default EditProduct;
